// @ts-nocheck
/**
 * Stool 3D Model Color Synchronization
 * Syncs color picker changes with the 3D model materials
 */

class Stool3DSync {
  constructor() {
    this.modelViewer = null;
    this.materials = {
      seat: null,
      leg1: null,
      leg2: null,
      leg3: null
    };
    this.viewerSetupAttempts = 0;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupViewer());
    } else {
      this.setupViewer();
    }

    // Also listen for an explicit ready signal from the product UI
    window.addEventListener('stool3DReady', () => {
      this.setupViewer();
    });
  }

  setupViewer() {
    // Look for the model viewer
    this.modelViewer = document.getElementById('stoolViewer');
    
    if (!this.modelViewer) {
      // Retry briefly in case the viewer is being injected
      if (this.viewerSetupAttempts < 10) {
        this.viewerSetupAttempts += 1;
        setTimeout(() => this.setupViewer(), 200);
      } else {
        console.log('3D model viewer not found');
      }
      return;
    }

    console.log('Found 3D model viewer:', this.modelViewer);

    // Wait for model to load
    this.modelViewer.addEventListener('load', () => this.onModelLoad());
    
    // If model is already available, proceed
    const mv = /** @type {any} */ (this.modelViewer);
    if (mv && mv.model) {
      this.onModelLoad();
    }
    
    // Also re-run material mapping when the viewer is revealed by UI
    window.addEventListener('stool3DReady', () => {
      const mv = /** @type {any} */ (this.modelViewer);
      if (mv && mv.model) {
        this.onModelLoad();
      }
    });
  }

  async onModelLoad() {
    try {
      // Get the model's materials
      const model = this.modelViewer.model;
      if (!model) {
        console.log('Model not available yet');
        return;
      }

      // Find materials by name
      const materials = await model.materials;
      
      // Reset mapping each time
      this.materials = { seat: null, leg1: null, leg2: null, leg3: null };

      // Robust mapping based on name hints
      const lowerNameToMat = materials.map(m => ({ name: (m.name || '').toLowerCase(), mat: m }));

      // Seat candidate names
      const seatCandidates = lowerNameToMat.filter(x => x.name.includes('tucci') || x.name.includes('teak') || x.name.includes('seat') || x.name.includes('wood'));
      if (seatCandidates.length > 0) {
        this.materials.seat = seatCandidates[0].mat;
      }

      // Leg candidates (powder coat/metal/leg)
      const legCandidates = lowerNameToMat
        .filter(x => x.name.includes('powder') || x.name.includes('coat') || x.name.includes('metal') || x.name.includes('leg'))
        .sort((a, b) => a.name.localeCompare(b.name));

      if (legCandidates.length >= 3) {
        this.materials.leg1 = legCandidates[0].mat;
        this.materials.leg2 = legCandidates[1].mat;
        this.materials.leg3 = legCandidates[2].mat;
      } else {
        // Fallback: assign any remaining non-seat materials
        const nonSeat = lowerNameToMat.filter(x => !seatCandidates.includes(x));
        this.materials.leg1 = nonSeat[0]?.mat || null;
        this.materials.leg2 = nonSeat[1]?.mat || null;
        this.materials.leg3 = nonSeat[2]?.mat || null;
      }

      console.log('3D Model materials mapped:', this.materials);
      
      // Setup color picker listeners
      this.setupColorListeners();
      
      // Apply initial colors
      this.applyInitialColors();
      
    } catch (error) {
      console.error('Error loading 3D model materials:', error);
    }
  }

  setupColorListeners() {
    // Listen for seat material changes
    const seatOptions = document.querySelectorAll('input[name="seat_material"]');
    seatOptions.forEach(radio => {
      radio.addEventListener('change', (e) => {
        console.log('Seat material changed to:', e.target.value);
        this.updateSeatMaterial(e.target.value);
      });
    });

    // Listen for leg color changes (color swatches)
    const legColorInputs = document.querySelectorAll('input[name="leg_colour"], input[name="leg_color_2"], input[name="leg_colour_3"]');
    legColorInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        console.log('Leg color changed:', e.target.name, e.target.value);
        this.updateLegColorFromSwatch(e.target);
      });
    });
  }

  updateSeatMaterial(materialType) {
    if (!this.materials.seat) {
      console.log('Seat material not found in 3D model');
      return;
    }

    try {
      // Define realistic wood colors and textures
      const woodMaterials = {
        light_wood: {
          color: [0.780, 0.671, 0.518],
          texture: 'https://cdn.shopify.com/s/files/1/0695/1387/1497/files/white-oak.jpg?v=1758575348'
        },
        dark_wood: {
          color: [0.35, 0.24, 0.18],
          texture: 'https://cdn.shopify.com/s/files/1/0695/1387/1497/files/white-oak.jpg?v=1758575348'
        }
      };

      const material = woodMaterials[materialType] || woodMaterials.light_wood;
      
      // Apply the wood color first
      this.materials.seat.pbrMetallicRoughness.setBaseColorFactor([...material.color, 1.0]);
      
      // Set wood material properties
      this.materials.seat.pbrMetallicRoughness.setRoughnessFactor(0.85);
      this.materials.seat.pbrMetallicRoughness.setMetallicFactor(0.0);
      
      // Load and apply wood texture using model-viewer's texture loader
      if (this.modelViewer && this.modelViewer.model) {
        this.modelViewer.model.createTexture(material.texture).then((texture) => {
          if (texture) {
            // Apply texture to the seat material
            this.materials.seat.pbrMetallicRoughness.setBaseColorTexture(texture);
            console.log(`Applied white oak texture with ${materialType} coloring`);
          }
        }).catch((error) => {
          console.log('Could not load texture via model-viewer, trying alternative method:', error);
          
          // Fallback: try direct image loading
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            try {
              // Create canvas texture
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              canvas.width = img.width;
              canvas.height = img.height;
              ctx.drawImage(img, 0, 0);
              
              // Apply via data URL
              const dataUrl = canvas.toDataURL();
              this.modelViewer.model.createTexture(dataUrl).then((texture) => {
                if (texture) {
                  this.materials.seat.pbrMetallicRoughness.setBaseColorTexture(texture);
                  console.log(`Applied wood texture via canvas fallback`);
                }
              });
            } catch (canvasError) {
              console.log('Canvas fallback failed, using color only:', canvasError);
            }
          };
          img.onerror = () => {
            console.log('Could not load wood texture image, using color only');
          };
          img.src = material.texture;
        });
      }
      
      console.log(`Updated wood seat to: ${materialType === 'light_wood' ? 'Light Oak' : 'Dark Oak'}`, material.color);
    } catch (error) {
      console.error('Error updating seat material:', error);
    }
  }

  updateLegColorFromSwatch(colorInput) {
    // Map input names to leg numbers
    let legNumber;
    if (colorInput.name === 'leg_colour') {
      legNumber = '1';
    } else if (colorInput.name === 'leg_color_2') {
      legNumber = '2';
    } else if (colorInput.name === 'leg_colour_3') {
      legNumber = '3';
    }
    
    if (!legNumber) {
      console.log('Unknown leg color input:', colorInput.name);
      return;
    }
    
    const materialKey = `leg${legNumber}`;
    const material = this.materials[materialKey];
    
    if (!material) {
      console.log(`Material for ${materialKey} not found`);
      return;
    }

    try {
      // Convert hex to RGB array (0-1 range)
      const hex = colorInput.value;
      const rgb = this.hexToRgb(hex);
      
      // Update the powder coat material color for legs
      material.pbrMetallicRoughness.setBaseColorFactor([rgb.r / 255, rgb.g / 255, rgb.b / 255, 1.0]);
      
      // Set powder coat finish properties for legs
      material.pbrMetallicRoughness.setMetallicFactor(0.1);  // Slight metallic sheen for powder coat
      material.pbrMetallicRoughness.setRoughnessFactor(0.25); // Smooth powder coat finish
      
      console.log(`Updated powder coated leg ${materialKey} to: ${hex}`, rgb);
    } catch (error) {
      console.error(`Error updating ${materialKey} color:`, error);
    }
  }

  applyInitialColors() {
    // Apply initial seat material
    const checkedSeat = document.querySelector('input[name="seat_material"]:checked');
    if (checkedSeat) {
      this.updateSeatMaterial(checkedSeat.value);
    }

    // Apply initial leg colors from swatches
    const legColorInputs = document.querySelectorAll('input[name="leg_colour"]:checked, input[name="leg_color_2"]:checked, input[name="leg_colour_3"]:checked');
    legColorInputs.forEach(input => {
      this.updateLegColorFromSwatch(input);
    });
  }

  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  }
}

// Initialize the 3D sync when the page loads
new Stool3DSync();
